import { useState } from "react";
import ICONS from "../../../../assets/Icons";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import TextInputField from "../../CommonInputFields/TextInputField";

/**
 * PAN details component used for PAN input, verification status,
 * and preview display.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean|null} props.verifiedPan - PAN verification status
 * @param {boolean} props.verifyPANisLoading - Loader state while verifying PAN
 * @param {string} props.panNumber - Entered PAN number
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} props.handlePanChange - PAN input change handler
 * @param {Object} props.panDetails - Verified PAN details object
 * @param {string} props.panDetails.full_name - Full legal name from PAN verification
 * @param {string} props.legalName - Legal name used in preview mode
 * @param {boolean} [props.previewMode=false] - Flag to render preview Static UI
 * @param {string} props.type - Component usage type (e.g. switch_account)
 * @returns {JSX.Element} PAN details UI component
 */

/**
 * Computes the input field variant based on
 * focus state, PAN length, and verification status.
 *
 * @function computeVariant
 * @param {string} value - Current PAN input value
 * @param {boolean} focused - Input focus state
 * @returns {string} Input variant type
 */

const PanDetails = ({
  verifiedPan,
  verifyPANisLoading,
  panNumber,
  handlePanChange,
  panDetails,
  legalName,
  previewMode = false,
  type,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;

    if (length === 0) return focused ? "typing" : "default";

    if (verifiedPan) return "verified";
    if (verifiedPan === false && !verifyPANisLoading) {
      return length === GlobalConfiguration.panLength
        ? "error"
        : focused
          ? "typing"
          : "default";
    }

    return focused
      ? "typing"
      : length === GlobalConfiguration.panLength && verifiedPan === false
        ? "error"
        : "default";
  };
  if (previewMode) {
    return (
      <div className="flex flex-col bg-primary-card rounded-border-radius-md gap-gap-md p-padding-md">
        <div className="text-text-primary font-semibold text-font-md">
          PAN Details
        </div>
        <div className="flex flex-col gap-gap-sm">
          <div className="flex flex-col gap-gap-xs">
            <div className="capitalize font-normal text-text-primary text-font-md">
              {panNumber}
            </div>
            <div className="text-font-xs font-normal text-text-secondary">
              PAN 
            </div>
          </div>
          <div className="bg-primary-bg h-[var(--border)] opacity-base-opacity my-margin-xs"></div>
          <div className="flex flex-col gap-gap-xs">
            <div className="w-width-full text-text-primary font-normal text-font-md text-wrap">
              {legalName}
            </div>
            <div className="text-font-xs font-normal text-text-secondary">
              Legal name
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-width-full rounded-border-radius-md relative z-z-index-header ${verifiedPan && panNumber && type === "switch_account" ? "opacity-light-opacity" : "opacity-base-opacity"}`}
    >
      <div className="w-width-full rounded-border-radius-md relative z-z-index-header">
        <div
          className={`absolute w-width-full py-padding-md bottom-bottom-base transition-all duration-animation-fast ease-in-out transform ${
            (!verifyPANisLoading && verifiedPan) || verifiedPan === false
              ? "rounded-b-none"
              : "rounded-b-border-radius-md"
          }`}
          style={{
            backgroundColor:
              verifiedPan && !verifyPANisLoading
                ? "var(--positive-card)"
                : verifiedPan === false && !verifyPANisLoading
                  ? "var(--negative-card)"
                  : "transparent",
          }}
        ></div>
        <div className="w-width-full flex flex-col gap-gap-md p-padding-md bg-primary-card rounded-border-radius-md relative">
          <div className="font-semibold text-md text-text-primary">
            PAN Details
          </div>
          <div>
            <TextInputField
              variant={computeVariant(panNumber, isFocused)}
              placeholder="PAN"
              bgColor="var(--primary-card)"
              fgColor="var(--text-primary)"
              value={panNumber}
              onInput={(e) => {
                handlePanChange(e);
              }}
              onFocus={(e) => setIsFocused(true)}
              onBlur={(e) => setIsFocused(false)}
              isFullWidth={true}
              adornment={
                verifyPANisLoading
                  ? ICONS?.LOADER_GIF
                  : verifiedPan === false &&
                      !verifyPANisLoading &&
                      panNumber.length === GlobalConfiguration.panLength
                    ? ICONS.ERROR_WITH_WHITE_OUTLINED_CIRCLE
                    : undefined
              }
              isDisabled={type === "switch_account"}
            />
          </div>
        </div>
      </div>
      <div
        className={`transition-transform duration-animation-fast ease-in-out transform ${
          (!verifyPANisLoading && verifiedPan) || verifiedPan === false
            ? "translate-y-0 opacity-base-opacity"
            : "-translate-y-full opacity-opacity-none"
        }`}
      >
        {!verifyPANisLoading && verifiedPan ? (
          <div
            className={`flex justify-between items-center px-padding-md py-padding-md rounded-b-border-radius-md bg-positive-card`}
          >
            <div className="flex justify-between items-start whitespace-nowrap overflow-hidden text-ellipsis text-text-primary">
              <img
                className="w-icon-md h-icon-md mr-margin-sm mt-margin-xs"
                src={ICONS?.VERIFIED_WITH_WHITE_CIRCLE_GREEN}
                alt="check"
              />
              <div className="flex flex-col gap-gap-xs">
                <div className="w-auto overflow-hidden text-ellipsis text-text-primary break-words whitespace-normal">
                  {panDetails?.full_name}
                </div>
                <div className="text-text-secondary text-left text-xs font-normal">
                  Legal name
                </div>
              </div>
            </div>
          </div>
        ) : verifiedPan === false ? (
          <div className="flex py-padding-md px-padding-md gap-gap-sm rounded-b-border-radius-md bg-negative-card">
            <img
              src={ICONS?.ERROR_WITH_WHITE_OUTLINED_CIRCLE}
              alt="redcheck"
              className="h-icon-md w-icon-md mt-margin-xs"
            />
            <div className="flex flex-col gap-gap-xs">
              <div className="w-auto overflow-hidden text-ellipsis text-text-primary break-words whitespace-normal">
                PAN is invalid
              </div>
              <div className="text-text-secondary text-left text-xs font-normal">
                To proceed enter a valid PAN.
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PanDetails;
